import GooglePlayAndAppStoreBadgeLinks from '@app-studio-web/ui/components/GooglePlayAndAppStoreBadgeLinks'
import { useAppConfig } from '@app-studio-web/ui/contexts/AppConfigContext'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Form, Link, useNavigate } from 'react-router-dom'

const TOTAL_YEARS = 60
const WEEKS_PER_YEAR = 52
const FILLED_WEEKS = 30 * WEEKS_PER_YEAR + Math.floor(WEEKS_PER_YEAR / 2)

function LifeCalendar() {
  const { t } = useTranslation()
  const grid = useMemo(() => {
    const total = TOTAL_YEARS * WEEKS_PER_YEAR
    const cells: ('filled' | 'empty' | 'last')[] = []
    for (let i = 0; i < total; i++) {
      if (i === FILLED_WEEKS) cells.push('last')
      else if (i < FILLED_WEEKS) cells.push('filled')
      else cells.push('empty')
    }
    return cells
  }, [])

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        className="grid w-fit gap-[2px]"
        style={{ gridTemplateColumns: `repeat(${WEEKS_PER_YEAR}, 1fr)` }}
      >
        {grid.map((filled, i) => (
          <div
            key={i}
            className={`h-[3px] w-[3px] rounded-[0.5px] md:h-[4px] md:w-[4px] md:rounded-[1px] ${
              filled === 'last'
                ? 'bg-[#c0392b]'
                : filled === 'filled'
                  ? 'bg-[#a89080]'
                  : 'bg-[#ddd6ce]'
            }`}
          />
        ))}
      </div>
      <div className="mt-4 max-w-xs space-y-1.5 md:max-w-sm">
        <p className="font-headline text-sm leading-snug text-[#6b6560] md:text-base">
          {t('pages.landing.lifeCalendar.heading')}
        </p>
        <p className="text-[11px] leading-relaxed text-[#a09890] md:text-xs">
          <Trans
            components={{
              highlight: <span className="font-semibold text-[#c0392b]" />,
            }}
            i18nKey="pages.landing.lifeCalendar.description"
          />
        </p>
      </div>
    </div>
  )
}

export default function LandingPage() {
  const { logo } = useAppConfig()
  const { t } = useTranslation()
  const navigate = useNavigate()

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    navigate('/onboarding')
  }

  return (
    <div className="bg-app-bg relative flex min-h-dvh flex-col overflow-hidden">
      {!('ReactNativeWebView' in window) && (
        <header className="relative z-10 mx-auto w-full max-w-5xl px-6 py-4">
          <div className="flex items-center justify-between">
            {logo}
            <div className="flex items-center gap-4">
              <Link
                className="font-headline text-custom-black text-[15px] font-bold hover:opacity-80"
                to="/account/sign-in"
                data-testid="button-login"
              >
                {t('shared.signIn')}
              </Link>
              <button
                onClick={() => navigate('/onboarding')}
                className="cursor-pointer rounded-xl bg-[#222222] px-5 py-2.5 text-sm font-bold text-white transition-all hover:brightness-110 active:scale-[0.98]"
              >
                {t('buttons.startNow')}
              </button>
            </div>
          </div>
        </header>
      )}

      <main className="relative z-10 mx-auto flex max-w-2xl flex-1 flex-col items-center justify-center gap-8 px-8 py-12">
        <div className="text-center">
          <h1
            className="font-headline text-custom-black text-[36px] leading-[1.1] font-normal tracking-tight md:text-[52px] lg:text-[60px]"
            data-testid="label-header"
          >
            {t('pages.landing.heading')}
          </h1>

          <p
            className="mx-auto mt-6 max-w-md text-[17px] leading-relaxed text-[#6b6560] md:text-[20px]"
            data-testid="label-subheading"
          >
            {t('pages.landing.subheading')}
          </p>

          <Form method="post" onSubmit={onSubmit}>
            <button
              className="mt-8 inline-flex cursor-pointer items-center gap-2 rounded-2xl bg-[#222222] px-10 py-4 text-lg font-bold text-white uppercase shadow-lg shadow-black/10 transition-all hover:shadow-xl hover:shadow-black/15 hover:brightness-110 active:scale-[0.98]"
              data-testid="button-continue"
              type="submit"
            >
              {t('buttons.startNow')}
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 10h12m0 0l-4-4m4 4l-4 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </Form>

          <div className="mt-8">
            <GooglePlayAndAppStoreBadgeLinks />
          </div>

          <div className="mt-10">
            <LifeCalendar />
          </div>
        </div>
      </main>

      <footer className="relative z-10 mt-auto px-6 py-6">
        <div className="flex flex-wrap items-center justify-center gap-x-3 text-base text-[#b5afa8]">
          <Link
            to="https://app-studio.ai/terms"
            className="transition-colors hover:text-[#6b6560]"
          >
            {t('footer.terms')}
          </Link>
          <span className="opacity-40">·</span>
          <Link
            to="https://app-studio.ai/privacy"
            className="transition-colors hover:text-[#6b6560]"
          >
            {t('footer.privacy')}
          </Link>
          <span className="opacity-40">·</span>
          <Link
            to="/help-center/contact"
            className="transition-colors hover:text-[#6b6560]"
          >
            {t('footer.contact', { defaultValue: 'Contact' })}
          </Link>
          <span className="opacity-40">·</span>
          <Link
            to="/help-center/cancel-subscription"
            className="transition-colors hover:text-[#6b6560]"
          >
            {t('footer.cancelSubscription', {
              defaultValue: 'How to Cancel Subscription',
            })}
          </Link>
        </div>
      </footer>
    </div>
  )
}
